import React from 'react';
import {Set} from 'immutable';
import Loading from 'components/pure/utils/Loading';
import CoachingPlanOverview from './CoachingPlanOverview';
import SubmitReviewFeedback from './SubmitReviewFeedback';
import ReviewSummary from './ReviewSummary';
import UpcomingReviewsActions from 'actions/coaching/UpcomingReviewsActions';
import CoachingReviewActions from 'actions/coaching/CoachingReviewActions';
import * as queryParams from 'utils/queryParams';
import CoachingPageHeader from 'components/coaching/CoachingPageHeader';
import ModifyReviewDialogContainer from 'components/coaching/ModifyReviewDialogContainer';
import CoachingPlanActions from 'actions/coaching/CoachingPlanActions';
import CoachingPlaylistActions from 'actions/coaching/CoachingPlaylistActions';
import InProgressReviewStore from 'stores/coaching/InProgressReviewStore';
import UPCOMING_REVIEW_ROLES from 'actions/coaching/UPCOMING_REVIEW_ROLES';

const rectifyPlaylist = ({autoStartReview, review, plan, coachingReviewTypes, isCoach}) => {
  // this will set us up to view the right plan, regardless of visibility
  CoachingPlaylistActions.setCurrentlyViewing.defer({planId: plan.id});

  const reviewType = coachingReviewTypes && coachingReviewTypes.get(review.reviewTypeId);
  const inProgressReview = InProgressReviewStore.getState().inProgressReviews.get(review.id + '');

  if (autoStartReview) {
    // autostart -> we must hide
    CoachingPlaylistActions.hide.defer();
  } else if (
    !plan.completed &&
    review.status !== 'COMPLETED' &&
    isCoach &&
    reviewType &&
    reviewType.includesCalls &&
    !inProgressReview
  ) {
    // if we can show (an upcoming review and youre the coach and its a call focused review)
    // show
    CoachingPlaylistActions.show.defer();
  } else {
    // if we shouldnt show (completed or not a call focused review or youre not the coach) - hide
    CoachingPlaylistActions.hide.defer();
  }
};

export default class CoachingPage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.goToReview = this.goToReview.bind(this);
    this.toggleModify = this.toggleModify.bind(this);
    this.onModifyReview = this.onModifyReview.bind(this);

    this.state = {
      showModifyDialog: props.showModifyDialog,
      editedKpis: Set(),
    };
  }

  componentDidMount() {
    const {autoStartReview, review, plan, isCoach} = this.props;

    // If this is an adhoc plan and we haven't completed the review,
    // then set the start time
    // non-adhoc plans are started either via `autoStartReview` query param
    // or by clicking the Start Review button
    if ((autoStartReview || plan.adHoc) && !review.completedAt) {
      CoachingReviewActions.startReview.defer(review.id, isCoach);
    }
    rectifyPlaylist(this.props);
  }

  setEditedKpis = kpis => this.setState({editedKpis: kpis});

  /**
   * Set which review is being shown
   */
  goToReview(review) {
    queryParams.set({reviewId: review.id}, true);
  }

  toggleModify() {
    this.setState({showModifyDialog: !this.state.showModifyDialog});
  }

  onModifyReview() {
    // Refresh the user's agenda in the event the modifications
    // affected one of them
    UpcomingReviewsActions.upcomingReviews(this.props.sessionUser.id, UPCOMING_REVIEW_ROLES.COACH);
    CoachingPlanActions.retrieve(this.props.plan.id, {includeReviews: true});
  }

  render() {
    const {
      plan,
      onFinishReview,
      onSelectPlan,
      onSelectContentDetail,
      rep,
      repId,
      contentDetailId,
      review,
      editFeedback,
      users,
      partiallyHydratedCalls,
      sessionUser,
      isReviewFeedbackLoading,
      reviewFileUploads,
      highLevelScores,
      coachingReviewTypes,
      autoStartReview,
      isCoach,
      isRecipient,
    } = this.props;

    if (!plan || !review) {
      return <Loading />;
    }

    const coachCanSubmit =
      !plan.completed &&
      (review.status === 'SCHEDULED' || (review.status === 'COMPLETED' && editFeedback));

    // Recipients can only submit before the review is completed.
    const recipientCanSubmit = !plan.completed && review.status !== 'COMPLETED';

    const showSubmitFeedback = (coachCanSubmit && isCoach) || (recipientCanSubmit && isRecipient);

    return (
      <div className="coaching-page dash-v2-coaching-page">
        <div className="coaching-page-body">
          <div className="main-section">
            <CoachingPageHeader
              rep={rep}
              plan={plan}
              autoStartReview={autoStartReview}
              onSelectPlan={onSelectPlan}
              sessionUser={sessionUser}
            />
            {!plan.adHoc && !plan.livecoaching && (
              <CoachingPlanOverview
                contentDetailId={contentDetailId}
                showPlanName={sessionUser.id !== repId}
                coachingPlan={plan}
                reviewId={review.id}
                onSelectContentDetail={onSelectContentDetail}
                repId={plan.recipient.id}
              />
            )}
            {showSubmitFeedback ? (
              // important that this checks false specifically, not falsy
              isReviewFeedbackLoading === false && (
                <SubmitReviewFeedback
                  review={review}
                  plan={plan}
                  users={users}
                  sessionUser={sessionUser}
                  coachingReviewTypes={coachingReviewTypes.toList()}
                  reviewFileUploads={reviewFileUploads}
                  calls={partiallyHydratedCalls}
                  onSubmit={onFinishReview}
                  adminId={this.props.adminId}
                  managerId={this.props.managerId}
                  coachId={this.props.coachId}
                  autoStartReview={autoStartReview}
                  toggleModify={this.toggleModify}
                  onModifyReview={this.onModifyReview}
                  isCoach={isCoach}
                  isRecipient={isRecipient}
                  editFeedback={editFeedback}
                  rectifyPlaylist={() => rectifyPlaylist(this.props)}
                  editedKpis={this.state.editedKpis}
                  setEditedKpis={this.setEditedKpis}
                />
              )
            ) : (
              <div className="reviews-with-calls">
                <ReviewSummary
                  plan={plan}
                  showDetails={true}
                  sessionUser={sessionUser}
                  review={review}
                  contentDetailId={contentDetailId}
                  reviewFileUploads={reviewFileUploads}
                  highLevelScores={highLevelScores}
                  users={users}
                  calls={partiallyHydratedCalls}
                  coachingReviewTypes={coachingReviewTypes}
                  coachingReviewAnswers={review.answers}
                  toggleModify={this.toggleModify}
                  isCoach={isCoach}
                  isRecipient={isRecipient}
                  editedKpis={this.state.editedKpis}
                  setEditedKpis={this.setEditedKpis}
                />
              </div>
            )}
          </div>
        </div>
        <ModifyReviewDialogContainer
          open={this.state.showModifyDialog}
          refreshCoachingData={this.onModifyReview}
          onRequestClose={this.toggleModify}
          review={review}
          // We need feedback to determine whether or not the type can change, so disallow
          // selection while loading.
          selectionDisabled={isReviewFeedbackLoading}
        />
      </div>
    );
  }
}
